import { AutocompleteAPI, DeckAPI, FetchAPI, FieldAPI, FilterAPI, TabItemAPI } from '@philips-internal/oswald-ui-components';
import Controller, { OnAfterFetch, OnClick, OnMount, OnSubmit } from '@philips-internal/oswald-ui-core/lib/client';
import { ClickEvent, FetchEvent, MountEvent, SubmitEvent } from '@philips-internal/oswald-ui-core/lib/component/types';
import { HttpService, MessageService } from '@philips-internal/oswald-ui-core/lib/services';
import { applicationContext } from '@philips-internal/oswald-ui-foundation';
import moment from 'moment';
import { ISO_DATE_FORMAT } from '@philips-internal/oswald-ui-model/lib/formatters/date/date-utils';
import { FragmentNames, MyChartMedicineAliases } from '../../codegen';
import { APPLICATION_ENDPOINTS } from '../../constants/application-endpoints';
import { APPLICATION_ROUTES } from '../../constants/application-routes';
import { MyChartMedicineTypes } from '../../constants/my-chart-medicine-types';
import UserContext from '../../utils/user-context';
import BaseController from '../BaseController';
import { Inject } from '@odinjs/odin';
import { Value } from '@philips-internal/oswald-ui-core';
import { defaultIfUndefined } from '../../utils/object-utils';
import { cast } from '../../utils/cast-helpers';
import { MyChartControlItems } from '../../constants/my-chart-tab-control-items';
import { MyChartTabStructure } from '../../constants/my-chart-tab-structure';

interface ComponentTarget {
  state: {
    alias: string;
  }
}

interface MedicineFilter {
  filterFromDate?: string;
  filterUntilDate?: string;
  situation?: MedicineSituationData;
}

interface MedicineSituationData {
  situationCode: string;
  situationDescription: string;
}

interface FetchResponseData {
  dose: string;
  endDate: string;
  medicament: string;
  read: string;
  sequence: number;
  situation: string;
  startDate: string;
}

@Controller({ fragment: FragmentNames.MY_CHART_MEDICINE })
export default class MyChartMedicineController extends BaseController {

  @Inject(MyChartMedicineAliases.FILTER_MEDICINE_LIST )
    filterListApi!: FilterAPI;

  @Inject(MyChartMedicineAliases.FETCH_HOSPITAL)
    fetchHospitalListApi!: FetchAPI;

  @Inject(MyChartMedicineAliases.FETCH_PATIENT)
    fetchPatientListApi!: FetchAPI;

  @Inject(MyChartMedicineAliases.TAB_HOSPITAL)
    tabHospitalListApi!: TabItemAPI;

  @Inject(MyChartMedicineAliases.DECK_HOSPITAL)
    deckHospitalAPI!: DeckAPI;

  @Inject(MyChartMedicineAliases.FILTER_FIELD_MEDICINE_SITUATION)
    filterFieldMedicineSituation!: FieldAPI;

  @Inject(MyChartMedicineAliases.AUTOCOMPLETE_SITUATION)
    autocompleteSituationApi!: AutocompleteAPI;

  @OnAfterFetch(MyChartMedicineAliases.FETCH_HOSPITAL)
  handleHospitalAfterFetch(event: FetchEvent): void {
    const data = defaultIfUndefined(event.data, []) as FetchResponseData[];
    const newData = data.map<FetchResponseData>(item => {
      return {
        ...item
      }
    });

    this.deckHospitalAPI.setData(cast(defaultIfUndefined(newData, [])));
  }

  @OnClick({ target: MyChartMedicineAliases.BUTTON_NEW_MEDICINE })
  handleBtnSendNewMedicine(): void {
    applicationContext.redirect(APPLICATION_ROUTES.MY_CHART_FORM_MEDICINE,  { medicineSequence: 'new', typeAction: 'new_medicament' });
  }

  @OnClick({ target: [ MyChartMedicineAliases.HOSPITAL_CARD, MyChartMedicineAliases.PATIENT_CARD ] })
  handleClickCard(event: ClickEvent): void {
    const item = event.value;
    const cardState = event.target as unknown as ComponentTarget;
    const cardType = this.getCardType(cardState.state.alias);
    const route = (cardType === MyChartMedicineTypes.HOSPITAL) ? APPLICATION_ROUTES.MY_CHART_MEDICINE_HOSPITAL_DETAILS : APPLICATION_ROUTES.MY_CHART_MEDICINE_PATIENT_DETAILS;

    if (cardType === MyChartMedicineTypes.HOSPITAL) {
      HttpService.post(APPLICATION_ENDPOINTS.POST_MC_MEDICINE_READ_DOCUMENT, { medicamentSequence: item.sequence });
    }

    applicationContext.redirect(route, { medicineSequence: item.sequence });
  }

  @OnClick({ target: [ MyChartMedicineAliases.TAB_PATIENT ] })
  handleInitialFilterByTabPatient(): void {
    const activeValue = 'A';
    const description = 'patient.text.active';

    const record = this.generateInitialFilter(activeValue, description);

    this.filterListApi.setData(record);
    this.context.set({medicineType: MyChartMedicineTypes.PATIENT});
  }

  @OnClick({ target: [ MyChartMedicineAliases.TAB_HOSPITAL ] })
  handleInitialFilterByTabHospital(): void {
    const activeValue = 'L';
    const description = 'patient.text.released';

    const record = this.generateInitialFilter(activeValue, description);

    this.filterListApi.setData(record);
    this.context.set({medicineType: MyChartMedicineTypes.HOSPITAL});
  }

  @OnMount({target: [ MyChartMedicineAliases.AUTOCOMPLETE_SITUATION ]})
  handleLoadAutocomplete(event: MountEvent): void {
    const type = this.context.get('medicineType');
    const autocomplete = event.target as AutocompleteAPI;

    autocomplete.setClearable(type === MyChartMedicineTypes.PATIENT);
    autocomplete.setEnabled(type === MyChartMedicineTypes.PATIENT);
  }

  @OnMount({ target: MyChartMedicineAliases.FILTER_MEDICINE_LIST })
  handleFilterMount(): void {
    const activeValue = 'A';
    const description = 'patient.text.active';

    const record = this.generateInitialFilter(activeValue, description);

    this.filterListApi.setData(record);
    this.filterListApi.submit();
  }

  @OnMount({ target: [ MyChartMedicineAliases.TAB_HOSPITAL ] })
  handleHospitalTabLoading(): void {
    const tabVisibility = this.context.get('tabVisibility') as MyChartTabStructure;
    if (tabVisibility.tabName === MyChartControlItems.MEDICINES_HOSPITAL_VISIBILITY) {
      this.tabHospitalListApi.setVisible(tabVisibility.visibility);
    }
  }

  @OnSubmit({ target: MyChartMedicineAliases.FILTER_MEDICINE_LIST })
  onSubmitFilter(response: SubmitEvent): void {
    const medicineFilter = response.record as MedicineFilter;
    const type = this.context.get('medicineType');
    const fetchApi = type === MyChartMedicineTypes.PATIENT ? this.fetchPatientListApi : this.fetchHospitalListApi;
    this.setParametersFetchList(medicineFilter, fetchApi);
  }

  @OnMount({ target: [ MyChartMedicineAliases.FETCH_PATIENT, MyChartMedicineAliases.FETCH_HOSPITAL ] })
  handleFetchMedicineLoading(event: MountEvent): void {
    const medicineFilter = this.getParametersFilterByContext();
    const fetchAPI = event.target as FetchAPI;
    this.setParametersFetchList(medicineFilter, fetchAPI);
  }

  private setParametersFetchList(medicineFilter: MedicineFilter, fetchAPI: FetchAPI): void {
    const type = this.context.get('medicineType') as string;
    const parameters = this.getParametersFilterForFetch(medicineFilter, type);

    this.setParametersFilterContext(medicineFilter);

    fetchAPI.setParameters(parameters);
    fetchAPI.forceFetch();
  }

  @OnMount({ target: [ MyChartMedicineAliases.DECK_PATIENT, MyChartMedicineAliases.DECK_HOSPITAL ] })
  handleMedicineDecksLoading(event: MountEvent): void {
    const deckAPI = event.target as DeckAPI;
    const type = deckAPI.getAlias() === MyChartMedicineAliases.DECK_PATIENT ? MyChartMedicineTypes.PATIENT : MyChartMedicineTypes.HOSPITAL;
    this.context.set({medicineType: type});
  }

  private generateInitialFilter = (code: string, description: string): Record<string, Value> => {
    const situation = {
      situationCode: code,
      situationDescription: MessageService.translate(description)
    } as MedicineSituationData;

    const filterFromDate = moment().subtract(60, 'd').format(ISO_DATE_FORMAT);
    const filterUntilDate = moment().format(ISO_DATE_FORMAT);

    this.context.set({
      params: {
        filterFromDate,
        filterUntilDate,
        situation,
      }
    });

    const record = {
      filterFromDate: filterFromDate,
      filterUntilDate: filterUntilDate,
      situation: situation
    } as Record<string, Value>;

    return record;
  }

  private setParametersFilterContext = (medicineFilter: MedicineFilter): void => {
    this.context.set({
      params: {
        ...medicineFilter
      }
    });
  }

  private getParametersFilterForFetch = (medicineFilter: MedicineFilter, medicineType: string): Record<string, Value> => {
    const { patientId } = UserContext;

    const parameters = {
      endDateFormat: medicineFilter?.filterUntilDate || '',
      personCode: patientId.toString(),
      startDateFormat: medicineFilter?.filterFromDate || '',
    } as Record<string, Value>;

    if(medicineType === MyChartMedicineTypes.PATIENT){
      parameters['situation'] = medicineFilter?.situation?.situationCode || 'ALL';
    }

    return parameters;
  }

  private getParametersFilterByContext = (): MedicineFilter => {
    const params = this.context.get('params') as MedicineFilter;

    if(params == null){
      return {};
    }

    const medicineFilter = {
      filterFromDate: params.filterFromDate,
      filterUntilDate: params.filterUntilDate,
      situation: params.situation,
    } as MedicineFilter;

    return medicineFilter;
  }

  private getCardType(alias: string): MyChartMedicineTypes {
    switch(alias) {
      case MyChartMedicineAliases.PATIENT_CARD:
        return MyChartMedicineTypes.PATIENT;
      case MyChartMedicineAliases.HOSPITAL_CARD:
        return MyChartMedicineTypes.HOSPITAL;
      default:
        throw Error(`The alias ${alias} not found`);
    }
  }
}
